import { useSyncExternalStore } from 'react'

/**
 * El tema del admin (claro / oscuro).
 *
 * Vive en `<html data-theme>` y no en un contexto de React a propósito: el
 * atributo tiene que estar puesto **antes del primer pintado**, y para eso lo
 * escribe un script en `index.html`. Si lo pusiera React, el usuario vería un
 * destello oscuro en cada carga. El hook de acá sólo lee y conmuta lo que ese
 * script ya dejó resuelto.
 *
 * `useSyncExternalStore` en vez de `useState` porque el conmutador puede estar
 * en más de un lugar (el pie del sidebar hoy, Configuración mañana) y todos
 * tienen que verse igual al instante. El store es el DOM, que es donde el tema
 * realmente está.
 *
 * El portal del cliente NO usa esto: tiene su propio conmutador sobre
 * `.portal-shell[data-theme]` y su propia clave (`portal_theme`). Son dos
 * aplicaciones distintas para dos personas distintas, y que el admin se ponga
 * en oscuro no es motivo para que el portal de un cliente cambie.
 */
export type Tema = 'light' | 'dark'

const LS_KEY = 'erp_theme'
const EVENTO = 'erp-theme-change'

/** El tema que está puesto ahora mismo, leído del DOM (la fuente de verdad). */
export function temaActual(): Tema {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
}

/**
 * Pone el tema: atributo, preferencia guardada y color de barra del navegador.
 *
 * El `theme-color` se actualiza porque en móvil e instalado como PWA es la
 * franja de arriba: con el ERP en claro y la franja azul marino, la pantalla
 * queda partida en dos.
 */
export function aplicarTema(tema: Tema): void {
  document.documentElement.setAttribute('data-theme', tema)
  try { localStorage.setItem(LS_KEY, tema) } catch { /* modo privado */ }

  const meta = document.querySelector('meta[name=theme-color]')
  if (meta) meta.setAttribute('content', tema === 'dark' ? '#0F1117' : '#F4F6FB')

  window.dispatchEvent(new Event(EVENTO))
}

function suscribir(alCambiar: () => void): () => void {
  window.addEventListener(EVENTO, alCambiar)
  // `storage` llega cuando el tema se cambió en OTRA pestaña del mismo ERP.
  window.addEventListener('storage', alCambiar)
  return () => {
    window.removeEventListener(EVENTO, alCambiar)
    window.removeEventListener('storage', alCambiar)
  }
}

/** `[tema, alternar]`. */
export function useTheme(): [Tema, () => void] {
  const tema = useSyncExternalStore(
    suscribir,
    temaActual,
    // En SSR/prerender no hay DOM; el claro es el defecto del admin.
    () => 'light' as Tema,
  )
  return [tema, () => aplicarTema(tema === 'dark' ? 'light' : 'dark')]
}
